import { ClerkProvider, useAuth, useOrganization, useUser } from "@clerk/tanstack-react-start"
import { auth, clerkClient } from "@clerk/tanstack-react-start/server"
import {
	HeadContent,
	Outlet,
	Scripts,
	createRootRouteWithContext,
	retainSearchParams,
	useRouter,
} from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
import type { ConvexReactClient } from "convex/react"
import { ConvexProviderWithClerk } from "convex/react-clerk"
import { useEffect } from "react"
import { RouteError } from "#/components/route-error"
import { Toaster } from "#/components/ui/sonner"
import { FeatureFlagProvider } from "#/lib/feature-flags"
import { identifyUser, initPostHog, resetUser, trackPageview } from "#/lib/posthog"
import type { Role } from "#/lib/roles"
import appCss from "../styles.css?url"

const THEME_INIT_SCRIPT = `(function(){try{var stored=window.localStorage.getItem('theme');var mode=(stored==='light'||stored==='dark'||stored==='auto')?stored:'auto';var prefersDark=window.matchMedia('(prefers-color-scheme: dark)').matches;var resolved=mode==='auto'?(prefersDark?'dark':'light'):mode;var root=document.documentElement;root.classList.remove('light','dark');root.classList.add(resolved);if(mode==='auto'){root.removeAttribute('data-theme')}else{root.setAttribute('data-theme',mode)}root.style.colorScheme=resolved;}catch(e){}})();`

interface RouterContext {
	convexClient: ConvexReactClient
}

const fetchClerkAuth = createServerFn({ method: "GET" }).handler(async () => {
	const { userId } = await auth()
	if (!userId) return { userId: null, roles: [] as Role[] }

	const client = await clerkClient()
	const user = await client.users.getUser(userId)
	const roles = (user.publicMetadata?.roles as Role[]) ?? []
	return { userId, roles }
})

export const Route = createRootRouteWithContext<RouterContext>()({
	validateSearch: (search: Record<string, unknown>): { demo?: string } => {
		const demo = typeof search.demo === "string" && search.demo ? search.demo : undefined
		return demo ? { demo } : {}
	},
	search: { middlewares: [retainSearchParams(["demo"])] },
	head: () => ({
		meta: [
			{ charSet: "utf-8" },
			{ name: "viewport", content: "width=device-width, initial-scale=1" },
			{ title: "Stoneflow Suite" },
		],
		links: [
			{ rel: "stylesheet", href: appCss },
			{ rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
			{ rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
			{ rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
			{ rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
			{ rel: "manifest", href: "/manifest.json" },
		],
	}),
	beforeLoad: async () => {
		const { userId, roles } = await fetchClerkAuth()
		return { userId, roles }
	},
	component: RootComponent,
	shellComponent: RootDocument,
	errorComponent: RouteError,
	notFoundComponent: () => <p>Page not found</p>,
})

function PostHogInit({ children }: { children: React.ReactNode }) {
	const { isSignedIn, userId } = useAuth()
	const { user } = useUser()
	const { organization } = useOrganization()
	const router = useRouter()

	useEffect(() => {
		initPostHog()
	}, [])

	useEffect(() => {
		if (isSignedIn && userId && user) {
			identifyUser(
				{
					id: userId,
					email: user.primaryEmailAddress?.emailAddress,
					name: user.fullName ?? undefined,
				},
				organization ? { id: organization.id, name: organization.name } : undefined,
			)
		} else if (isSignedIn === false) {
			resetUser()
		}
	}, [isSignedIn, userId, user, organization])

	useEffect(() => {
		return router.subscribe("onResolved", ({ toLocation }) => {
			trackPageview(toLocation.href)
		})
	}, [router])

	return <>{children}</>
}

function RootComponent() {
	const { convexClient } = Route.useRouteContext()

	return (
		<ClerkProvider>
			<ConvexProviderWithClerk client={convexClient} useAuth={useAuth}>
				<FeatureFlagProvider>
					<PostHogInit>
						<Outlet />
						<Toaster />
					</PostHogInit>
				</FeatureFlagProvider>
			</ConvexProviderWithClerk>
		</ClerkProvider>
	)
}

function RootDocument({ children }: { children: React.ReactNode }) {
	return (
		<html lang="en" suppressHydrationWarning>
			<head>
				{/* biome-ignore lint/security/noDangerouslySetInnerHtml: theme init script is a trusted static string */}
				<script dangerouslySetInnerHTML={{ __html: THEME_INIT_SCRIPT }} />
				<HeadContent />
			</head>
			<body className="font-sans antialiased [overflow-wrap:anywhere]">
				{children}
				<Scripts />
			</body>
		</html>
	)
}
